import { useEffect, useRef, useCallback } from "react";
import { useUserManagementStore } from "@presentation/stores/UserManagmentStore";
import { useInjection } from "@presentation/components/dependencyInjection/useInjection";
import { AuthService } from "@infrastructure/services";
import { useNotifications } from "@presentation/components/notifications";
import type { UserSession } from "@domain/entities";

const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes

const isEmptySession = (s: UserSession): boolean => {
  const hasUsername = !!s.username;
  const hasAccessToken = !!s.accessToken;
  const hasXsrf = "xsrfToken" in s && !!s.xsrfToken;
  return !hasUsername && !hasAccessToken && !hasXsrf;
};

export const useInactivityAutoSignOutHook = () => {
  const { pushNotification } = useNotifications();
  const authService = useInjection(AuthService);

  // Subscribe so effect reacts to login/logout
  const userSession = useUserManagementStore((st) => st.userSession);
  const clearSession = useUserManagementStore((st) => st.clear);

  const timeoutReference = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasNotifiedReference = useRef<boolean>(false); // ensure message only once per logout

  const clearTimer = useCallback((): void => {
    if (timeoutReference.current === null) {
      return;
    }

    clearTimeout(timeoutReference.current);
    timeoutReference.current = null;
  }, []);

  const scheduleTimer = useCallback((): void => {
    clearTimer();

    // Don’t arm if already logged out
    const current = useUserManagementStore.getState().userSession;
    if (isEmptySession(current)) return;

    timeoutReference.current = setTimeout(async () => {
      const now = useUserManagementStore.getState().userSession;

      // If already logged out or already notified, do nothing
      if (isEmptySession(now) || hasNotifiedReference.current) {
        clearTimer();
        return;
      }

      // Mark notified so we don’t show it twice
      hasNotifiedReference.current = true;

      console.warn("User inactive for 15 minutes. Signing out...");
      clearSession();
      try {
        await authService.signOut();
      } catch {
        // ignore; signOut should be idempotent
      }
      pushNotification("You were signed out due to inactivity.", "warning");

      clearTimer();
    }, INACTIVITY_LIMIT_MS);
  }, [authService, clearSession, clearTimer, pushNotification]);

  const resetTimer = useCallback((): void => {
    const s = useUserManagementStore.getState().userSession;
    if (isEmptySession(s)) {
      // When logged out, stop timers and reset one-shot flag for next login
      hasNotifiedReference.current = false;
      clearTimer();
      return;
    }
    scheduleTimer();
  }, [scheduleTimer, clearTimer]);

  useEffect(() => {
    const events: Array<keyof WindowEventMap> = ["mousemove", "keydown", "scroll", "touchstart"];

    if (isEmptySession(userSession)) {
      for (const e of events) removeEventListener(e, resetTimer);
      clearTimer();
    } else {
      for (const e of events) addEventListener(e, resetTimer, { passive: true });
      resetTimer(); // start/refresh
    }

    return () => {
      for (const e of events) removeEventListener(e, resetTimer);
      clearTimer();
    };
  }, [userSession, resetTimer, clearTimer]);
};
